// src/components/intake/api.js
//
// The print intake's calls to backend/routes/intake.py, plus the drafts and
// quote endpoints it reads. Every call throws an Error carrying the server's
// own message, its status and its body, so a screen can show the message as is.

import { API_BASE } from '../../utils/api';

function headers(extra) {
  const token = localStorage.getItem('rv_access_token');
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(extra || {}),
  };
}

async function request(path, { method = 'GET', json, form } = {}) {
  const init = {
    method,
    credentials: 'omit',
    headers: headers(json !== undefined ? { 'Content-Type': 'application/json' } : undefined),
  };
  if (json !== undefined) init.body = JSON.stringify(json);
  if (form) init.body = form;

  const res = await fetch(`${API_BASE}${path}`, init);
  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok) {
    const err = new Error((data && data.error) || `Request failed (${res.status})`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

// Files go as multipart with the fields in a JSON `payload` part; without files
// the same fields go as a plain JSON body. The server reads either.
function submissionBody(payload, files) {
  const present = Object.entries(files || {}).filter(([, file]) => file);
  if (!present.length) return { json: payload };
  const form = new FormData();
  form.append('payload', JSON.stringify(payload));
  present.forEach(([field, file]) => form.append(field, file));
  return { form };
}

export const intakeApi = {
  // Only the books this author wrote: a collaborator can edit a book, but the
  // print order is the author's to place.
  myBooks: () => request('/api/drafts?type=manuscript&authored=true'),
  book: (id) => request(`/api/drafts/${encodeURIComponent(id)}`),
  mySubmissions: (draftId) =>
    request(`/api/intake/submissions/mine${draftId ? `?draft_id=${encodeURIComponent(draftId)}` : ''}`),
  pricing: () => request('/api/quotes/pricing'),
  quote: (selections) => request('/api/quotes/calculate', { method: 'POST', json: { selections } }),
  submit: (payload, files) =>
    request('/api/intake/submissions', { method: 'POST', ...submissionBody(payload, files) }),
  submission: (ref) => request(`/api/intake/submissions/${encodeURIComponent(ref)}`),
  // A Word, text, Markdown, web page or EPUB manuscript, with its cover,
  // becomes a ReVisionari book; nothing is submitted.
  importBook: ({ manuscript, cover }) => {
    const form = new FormData();
    form.append('manuscript', manuscript);
    form.append('cover', cover);
    return request('/api/intake/import', { method: 'POST', form });
  },
  resubmit: (ref, payload, files) =>
    request(`/api/intake/submissions/${encodeURIComponent(ref)}/resubmit`, {
      method: 'POST',
      ...submissionBody(payload, files),
    }),
  queue: (status) => request(`/api/intake/queue?status=${encodeURIComponent(status)}`),
  coverSize: (specs) => {
    const query = new URLSearchParams();
    ['size', 'pages', 'paper_type', 'binding', 'cover_type'].forEach((key) => {
      if (specs && specs[key] != null) query.set(key, specs[key]);
    });
    return request(`/api/intake/cover-size?${query.toString()}`);
  },

  // The author's book as it would print at `size`, as a PDF blob URL the page
  // can show. The caller revokes it.
  async previewUrl(draftId, size) {
    const res = await fetch(
      `${API_BASE}/api/intake/preview/${encodeURIComponent(draftId)}?size=${encodeURIComponent(size || '')}`,
      { credentials: 'omit', headers: headers() }
    );
    if (!res.ok) {
      let message = `Request failed (${res.status})`;
      try {
        message = (await res.json()).error || message;
      } catch {
        // Not JSON: keep the status message.
      }
      throw new Error(message);
    }
    return URL.createObjectURL(await res.blob());
  },
  approve: (ref, notes) =>
    request(`/api/intake/submissions/${encodeURIComponent(ref)}/approve`, { method: 'POST', json: { notes } }),
  sendBack: (ref, notes) =>
    request(`/api/intake/submissions/${encodeURIComponent(ref)}/send-back`, { method: 'POST', json: { notes } }),

  // The PDF needs the Authorization header, which a plain link cannot send, so
  // it is fetched and handed to the browser as a download.
  async downloadBookPdf(ref, filename) {
    const res = await fetch(`${API_BASE}/api/intake/submissions/${encodeURIComponent(ref)}/book.pdf`, {
      credentials: 'omit',
      headers: headers(),
    });
    if (!res.ok) {
      let message = `Request failed (${res.status})`;
      try {
        message = (await res.json()).error || message;
      } catch {
        // Not JSON: keep the status message.
      }
      throw new Error(message);
    }
    const url = URL.createObjectURL(await res.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  },
};
